import { useState } from 'react';
import { TbDotsVertical } from 'react-icons/tb';

import { Button } from '@frontend/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@frontend/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend/components/ui/tooltip';
import { useDeleter } from '@frontend/shared/components/Question/hooks/Question.hooks.useDeleter';
import { QuestionContext } from '@frontend/shared/components/Question/QuestionContext';
import { useIsEditor } from '@frontend/shared/hooks/shared.hook.useIsEditor';
import { useDirectoryTeam } from '@frontend/shared/hooks/useDirectory';
import { useRequiredContext } from '@frontend/shared/hooks/useRequiredContext';
import { Copy, Trash2, X } from 'lucide-react';

export function QuestionActions({ onEdit }: { onEdit: () => void }) {
  const team = useDirectoryTeam();
  const [isOpen, setIsOpen] = useState(false);

  const isEditor = useIsEditor();

  const editHandler = () => {
    onEdit();
    setIsOpen(false);
  };

  const question = useRequiredContext(QuestionContext);

  const { isDeleting, onConfirmDelete, confirmer } = useDeleter(question.id);

  const visibleUrl = `${window.location.host}/q/${question.id}`;
  const url = `${window.location.origin}/q/${question.id}?team=${team.id}`;

  return (
    <>
      {confirmer}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
            }}
            disabled={isDeleting}
            className="ml-auto w-8 h-8 flex-none pr-0"
            size="icon"
            variant="ghost"
            aria-label="Learn more"
          >
            {isDeleting ? '...' : <TbDotsVertical className="h-4 w-4" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-96 p-0"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-[rgba(3,52,38,0.1)] bg-[rgba(3,52,38,0.03)] text-[rgba(3,52,38,1)] tracking-[-0.01em] font-medium px-4 h-14 text-base relative leading-6 flex items-center justify-between">
            <span>Details</span>
            <Button
              onClick={() => setIsOpen(false)}
              variant="ghost"
              size="icon"
              className="h-6 w-6 absolute top-1/2 -translate-y-1/2 right-4"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="px-8 py-6">
            <h3 className="text-[13px] leading-6 font-medium text-gray-600 tracking-[-0.01em]">
              Views
            </h3>
            <p className="text-5xl leading-none font-medium text-gray-900 tracking-[-0.06em] mt-2">
              {question.view_count}
            </p>

            <h3 className="text-[13px] leading-6 font-medium text-gray-600 tracking-[-0.01em] mt-6">
              Share this answer
            </h3>
            <div className="flex items-center">
              <Button
                asChild
                variant="link"
                size="sm"
                className="text-[#2563eb] mr-1 p-0 h-auto"
              >
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {visibleUrl}
                </a>
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigator.clipboard.writeText(url)}
                    aria-label="Copy URL to clipboard"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Copy URL to clipboard</TooltipContent>
              </Tooltip>
            </div>
          </div>
          {isEditor && (
            <div className="mt-5 p-4">
              <div className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      onClick={editHandler}
                      className="bg-[#10b981] hover:bg-[#059669] text-white"
                    >
                      Edit
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit question</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="ml-auto"
                      aria-label="Delete question"
                      variant="destructive"
                      size="sm"
                      onClick={onConfirmDelete}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete question</TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}
